/**
 * Writing module state hook.
 *
 * Manages state for writing module including essay content, fill-in-the-blank answers,
 * chunk navigation, timer, and answer submission. Fetches real data from backend API.
 *
 * @module useWritingModuleState
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { fetchWritingModuleData } from '@/lib/examApi';
import {
  transformWritingModuleData,
  type EssayTaskData,
  type FillBlankFormData,
} from '@/utils/writingDataTransform';
import { buildWritingAnswerSubmissions } from '@/utils/writingAnswerSubmission';
import { useModuleStateBase } from './useModuleStateBase';
import { useModuleData } from './useModuleData';
import { useTimer } from './useTimer';
import { useAuth } from './useAuth';
import { useSimulationDraft } from './useSimulationDraft';
import { readDraftQuestionOrder } from '@/lib/simulationDraftStorage';
import { logger } from '@/lib/logger';
import { getWritingNotesTaskKey } from '@/utils/simulationQuestionNavigation';
import type {
  ExamType,
  CEFRLevel,
  SimulationSettings,
} from '@/types/exam-simulation';

/**
 * Options for the writing module state hook.
 */
interface UseWritingModuleStateOptions {
  /** Exam type (goethe, osd, or telc) */
  examType: ExamType;
  /** CEFR proficiency level (A1, A2, B1, B2, C1, or C2) */
  level: CEFRLevel;
  /** Simulation settings from API (null if not yet loaded) */
  settings: SimulationSettings | null;
  /** Current user ID (optional, used for session tracking) */
  userId?: string | null;
  /** Session ID passed from parent (if provided, skips local generation) */
  sessionId?: string | null;
}

/**
 * Writing module state
 */
interface WritingModuleState {
  /** Current chunk index (0: fill-in-the-blank when present, then one chunk per essay task) */
  currentChunk: number;
  /** Essay content keyed by essay task questionId */
  essayContentByTask: Record<string, string>;
  /** Fill-in-the-blank answers keyed by blank ID */
  fillBlankAnswers: Record<string, string>;
  /** Scratchpad notes keyed by writing task */
  notesByTask: Record<string, string>;
  /** Time remaining in seconds */
  timeRemaining: number;
}

/**
 * Return type for writing module state hook
 */
interface UseWritingModuleStateReturn {
  /** Current writing module state */
  state: WritingModuleState;
  /** All essay tasks for this module (one per essay question, in section order) */
  essayTasks: EssayTaskData[];
  /** The essay task the learner is currently viewing (undefined when on fill-in-blank chunk or no essays) */
  currentEssayTask?: EssayTaskData;
  /** Essay content for the currently displayed essay task (empty string when none) */
  currentEssayContent: string;
  /** Fill-in-blank forms (for WritingFormPanel) */
  fillBlankForms: FillBlankFormData[];
  /** Whether fill-in-blank questions are available */
  hasFillBlank: boolean;
  /** Whether essay questions are available */
  hasEssay: boolean;
  /** Total number of chunks (fill-in-blank chunk + one per essay task) */
  totalChunks: number;
  /** Whether data is currently loading */
  isLoadingData: boolean;
  /** Error object if data fetch failed */
  dataError: Error | null;
  /** Session ID for answer tracking */
  sessionId: string | null;
  /** Scratchpad notes for the current writing task */
  currentNotes: string;
  /** Update essay content for the currently displayed essay task */
  setCurrentEssayContent: (content: string) => void;
  /** Function to update fill-in-the-blank answer */
  setFillBlankAnswer: (blankId: string, value: string) => void;
  /** Function to update scratchpad notes for the current writing task */
  setCurrentNotes: (notes: string) => void;
  /** Function to navigate between chunks */
  navigateChunk: (direction: 'prev' | 'next') => void;
  /** Jump to a chunk by index (0-based) */
  goToChunk: (index: number) => void;
  /** Function to save answers and finish module */
  saveAnswersAndResult: () => Promise<void>;
}

/**
 * Hook for managing writing module state and interactions.
 *
 * Provides comprehensive state management for the writing exam module including:
 * - Data fetching and transformation from backend
 * - Answer tracking and submission
 * - Timer management
 * - Chunk navigation (essay vs fill-in-the-blank)
 *
 * Automatically fetches writing module data when settings are available and
 * transforms it into the frontend format. Manages timer countdown and provides
 * functions for saving answers and completing the module.
 *
 * @param options - Configuration options for the hook
 * @returns Object containing state, data, and interaction functions
 *
 * @example
 * ```typescript
 * const {
 *   state,
 *   currentEssayTask,
 *   currentEssayContent,
 *   fillBlankForms,
 *   setCurrentEssayContent,
 *   saveAnswersAndResult
 * } = useWritingModuleState({
 *   examType: 'goethe',
 *   level: 'B1',
 *   settings: simulationSettings,
 *   userId: user?.id
 * });
 * ```
 */
export function useWritingModuleState({
  examType,
  level,
  settings,
  userId: optionalUserId,
  sessionId: externalSessionId,
}: UseWritingModuleStateOptions): UseWritingModuleStateReturn {
  const { user } = useAuth();

  // Resolve actual user ID from options or auth context
  const userId = optionalUserId || user?.id || null;

  // Use base hook for shared functionality (session ID)
  const {
    sessionId,
  } = useModuleStateBase({
    module: 'writing',
    examType,
    level,
    settings,
    userId,
    calculateCorrectness: false, // Writing module doesn't calculate correctness
    sessionId: externalSessionId,
  });

  // Resume: pin the exact task(s) saved in the draft so the learner sees the
  // same essay/fill-in-blank task they were answering, instead of a freshly
  // reshuffled alternate. Read once on mount; fresh runs have no draft and fall
  // through to the normal selection (pins undefined).
  const [pinnedQuestionIds] = useState<string[] | undefined>(() =>
    readDraftQuestionOrder(userId, sessionId, 'writing'),
  );

  // Fetch writing module data using shared hook
  const { data: simulationData, isLoading: isLoadingData, error: dataError } = useModuleData({
    module: 'writing',
    examType,
    level,
    settings,
    userId,
    pinnedQuestionIds,
    fetchFn: fetchWritingModuleData,
  });

  // Transform backend data to writing module format
  const writingData = useMemo(() => {
    if (!simulationData) {
      return {
        essayTasks: [],
        fillBlankForms: [],
        fillBlankSection: 1,
        hasFillBlank: false,
        hasEssay: false,
      };
    }
    return transformWritingModuleData(simulationData);
  }, [simulationData]);

  // Get initial time from settings or use default (20 minutes)
  const initialTime = useMemo(() => {
    if (settings?.moduleTimings?.writing) {
      return settings.moduleTimings.writing * 60; // Convert minutes to seconds
    }
    return 20 * 60; // Default 20 minutes
  }, [settings]);

  /**
   * When rehydrating from a draft with `remainingAtSave`, use that as the
   * countdown budget so time does not elapse while the learner is outside the simulation.
   */
  const [resumedBudgetSeconds, setResumedBudgetSeconds] = useState<number | null>(null);
  const effectiveInitialTime = resumedBudgetSeconds ?? initialTime;

  /**
   * Wall-clock anchor for the writing timer, set once on first mount unless
   * rehydrate restores a legacy anchor or a fresh anchor after pause-aware resume.
   */
  const [timerStartedAt, setTimerStartedAt] = useState<number | null>(null);
  useEffect(() => {
    if (timerStartedAt === null) {
      setTimerStartedAt(Date.now());
    }
  }, [timerStartedAt]);

  const { timeRemaining, reset: resetTimer } = useTimer({
    initialTime: effectiveInitialTime,
    enabled: true,
    autoStart: true,
    startedAt: timerStartedAt,
  });

  const [currentChunk, setCurrentChunk] = useState(0);
  const [essayContentByTask, setEssayContentByTaskState] = useState<Record<string, string>>({});
  const [fillBlankAnswers, setFillBlankAnswersState] = useState<Record<string, string>>({});
  const [notesByTask, setNotesByTaskState] = useState<Record<string, string>>({});

  useEffect(() => {
    if (timerStartedAt === null) {
      resetTimer(effectiveInitialTime);
    }
  }, [effectiveInitialTime, resetTimer, timerStartedAt]);

  // Derive the currently displayed essay task from `currentChunk`.
  // Layout: chunk 0 = fill-in-blank (if present); chunks (hasFillBlank?1:0)..N
  // map 1:1 onto essay tasks in `writingData.essayTasks` order.
  const essayBaseChunk = writingData.hasFillBlank ? 1 : 0;
  const currentEssayTask = useMemo(() => {
    if (!writingData.essayTasks.length) return undefined;
    if (currentChunk < essayBaseChunk) return undefined;
    return writingData.essayTasks[currentChunk - essayBaseChunk];
  }, [currentChunk, essayBaseChunk, writingData.essayTasks]);

  const currentEssayContent = currentEssayTask
    ? essayContentByTask[currentEssayTask.questionId] ?? ''
    : '';

  const currentNotesTaskKey = useMemo(
    () =>
      getWritingNotesTaskKey({
        currentChunk,
        hasFillBlank: writingData.hasFillBlank,
        hasEssay: writingData.hasEssay,
        fillBlankQuestionId: writingData.fillBlankForms[0]?.questionId,
        essayQuestionId: currentEssayTask?.questionId,
      }),
    [
      currentChunk,
      writingData.hasFillBlank,
      writingData.hasEssay,
      writingData.fillBlankForms,
      currentEssayTask,
    ],
  );

  const currentNotes = notesByTask[currentNotesTaskKey] ?? '';

  // --- Draft persistence ----------------------------------------------------
  const draftPayload = useMemo(
    () => ({
      currentChunk,
      essayContentByTask,
      fillBlankAnswers,
      notesByTask,
      // Stable task identity: persist every essay task + the displayed
      // fill-in-blank question ID so resume pins them and the backend can't
      // promote a different alternate to position [0] on the next (reshuffled)
      // fetch.
      questionOrder: [
        ...writingData.essayTasks.map((t) => t.questionId),
        writingData.fillBlankForms[0]?.questionId,
      ].filter((id): id is string => typeof id === 'string'),
    }),
    [currentChunk, essayContentByTask, fillBlankAnswers, notesByTask, writingData],
  );

  const hasUnsavedChanges = useMemo(() => {
    const hasEssayContent = Object.values(essayContentByTask).some(
      (text) => text.trim().length > 0,
    );
    const hasFillBlankAnswerState = Object.keys(fillBlankAnswers).length > 0;
    const hasNotesState = Object.keys(notesByTask).length > 0;
    return hasEssayContent || hasFillBlankAnswerState || hasNotesState;
  }, [essayContentByTask, fillBlankAnswers, notesByTask]);

  const draft = useSimulationDraft({
    userId,
    sessionId,
    module: 'writing',
    step: 'module',
    examType,
    level,
    timer:
      timerStartedAt === null
        ? null
        : {
            initialTimeSeconds: initialTime,
            startedAt: timerStartedAt,
            remainingAtSave: timeRemaining,
          },
    payload: draftPayload,
    shouldPersist: () => hasUnsavedChanges,
    onRehydrate: (envelope) => {
      const payload = envelope.payload as {
        currentChunk?: number;
        // New shape: per-task essay content keyed by questionId.
        essayContentByTask?: Record<string, string>;
        // Legacy shape: single essay string (older drafts before multi-essay).
        essayContent?: string;
        fillBlankAnswers?: Record<string, string>;
        notesByTask?: Record<string, string>;
      };
      if (typeof payload.currentChunk === 'number') setCurrentChunk(payload.currentChunk);
      if (payload.essayContentByTask && typeof payload.essayContentByTask === 'object') {
        setEssayContentByTaskState(payload.essayContentByTask);
      } else if (typeof payload.essayContent === 'string') {
        // Legacy draft: surface the saved essay text against the first essay
        // task we have on hand. If the task list hasn't loaded yet, stash it
        // under a sentinel key and let the next save replace it.
        const firstEssayId = writingData.essayTasks[0]?.questionId;
        if (firstEssayId) {
          setEssayContentByTaskState({ [firstEssayId]: payload.essayContent });
        }
      }
      if (payload.fillBlankAnswers && typeof payload.fillBlankAnswers === 'object') {
        setFillBlankAnswersState(payload.fillBlankAnswers);
      }
      if (payload.notesByTask && typeof payload.notesByTask === 'object') {
        setNotesByTaskState(payload.notesByTask);
      }
      if (envelope.timer) {
        if (envelope.timer.remainingAtSave == null) {
          setResumedBudgetSeconds(null);
          setTimerStartedAt(envelope.timer.startedAt);
        } else {
          setResumedBudgetSeconds(envelope.timer.remainingAtSave);
          setTimerStartedAt(Date.now());
        }
      }
    },
  });

  const writingSaveIndicatorLastAtRef = useRef(Date.now());
  const WRITING_SAVE_INDICATOR_MS = 10_000;

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }
    const now = Date.now();
    if (now - writingSaveIndicatorLastAtRef.current < WRITING_SAVE_INDICATOR_MS) {
      return;
    }
    writingSaveIndicatorLastAtRef.current = now;
    draft.requestSaveIndicator();
  }, [essayContentByTask, fillBlankAnswers, notesByTask, hasUnsavedChanges, draft]);

  // Calculate total chunks dynamically based on available question types.
  // Layout: one chunk for fill-in-blank (when present) + one chunk per essay
  // task. The essay-task count follows the backend, with no level branching.
  const totalChunks = useMemo(() => {
    return (writingData.hasFillBlank ? 1 : 0) + writingData.essayTasks.length;
  }, [writingData.hasFillBlank, writingData.essayTasks.length]);

  // Update essay content for the currently displayed essay task.
  const currentEssayQuestionId = currentEssayTask?.questionId;
  const setCurrentEssayContent = useCallback(
    (content: string) => {
      if (!currentEssayQuestionId) return;
      setEssayContentByTaskState((prev) => ({
        ...prev,
        [currentEssayQuestionId]: content,
      }));
    },
    [currentEssayQuestionId],
  );

  // Update fill-in-the-blank answer
  const setFillBlankAnswer = useCallback((blankId: string, value: string) => {
    setFillBlankAnswersState((prev) => ({
      ...prev,
      [blankId]: value,
    }));
  }, []);

  const setCurrentNotes = useCallback(
    (notes: string) => {
      setNotesByTaskState((prev) => ({
        ...prev,
        [currentNotesTaskKey]: notes,
      }));
    },
    [currentNotesTaskKey],
  );

  // Navigate between chunks
  const navigateChunk = useCallback(
    (direction: 'prev' | 'next') => {
      setCurrentChunk((prev) => {
        if (direction === 'next' && prev < totalChunks - 1) {
          return prev + 1;
        }
        if (direction === 'prev' && prev > 0) {
          return prev - 1;
        }
        return prev;
      });
    },
    [totalChunks],
  );

  /** Jump directly to a chunk index (e.g. task navigator). */
  const goToChunk = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalChunks) {
        setCurrentChunk(index);
      }
    },
    [totalChunks],
  );

  // Save answers and result
  const saveAnswersAndResult = useCallback(async () => {
    if (!simulationData || !sessionId || !userId) {
      logger.warn('Cannot save writing module answers - missing required data', {
        event: 'simulation.writing.missing_context',
        hasSimulationData: !!simulationData,
        hasSessionId: !!sessionId,
        hasUserId: !!userId,
        userId,
        sessionId,
      });
      return;
    }

    const { saveUserAnswersBatch, saveSimulationResult } = await import('@/lib/examApi');

    // Persist a row for every writing question (essay + each fill-in-the-blank),
    // even when the learner submitted nothing for it. This mirrors the speaking
    // and reading/listening modules and ensures the AI analyser sees every
    // section so an entirely-skipped section is graded as 0 instead of being
    // silently dropped from the average (which previously let a user "pass"
    // the module by only answering one section).
    const answers = buildWritingAnswerSubmissions({
      userId,
      sessionId,
      essayTasks: writingData.essayTasks,
      essayContentByTask,
      fillBlankForms: writingData.fillBlankForms,
      fillBlankAnswers,
    });

    try {
      // Save answers if any exist
      if (answers.length > 0) {
        logger.event(
          'simulation.writing.answers.save.started',
          {
            sessionId,
            userId,
            answerCount: answers.length,
            questionIds: answers.map((answer) => answer.question_id),
          },
          'info',
          'Writing answers save started',
        );
        logger.debug(`Saving ${answers.length} writing answers before result`, {
          sessionId,
          answerCount: answers.length,
        });
        await saveUserAnswersBatch({ answers });
        logger.debug('Writing answers saved successfully');
      } else {
        logger.debug('No answers to save, proceeding to save simulation result', {
          sessionId,
        });
      }

      // Always save simulation result, even if no answers
      // This ensures analysis can still be triggered (though it will find no answers)
      // This is important for timeout scenarios where user may not have answered anything
      logger.debug('Saving simulation result (will enqueue AI analysis)', {
        sessionId,
        module: 'writing',
        level,
      });
      logger.event(
        'simulation.writing.result.save.started',
        {
          sessionId,
          userId,
          level,
          examType,
          answerCount: answers.length,
        },
        'info',
        'Writing result save started',
      );
      const result: {
        session_id: string;
        user_id: string;
        level: string;
        is_graded: boolean;
        module: string;
        exam_type: string;
        is_success?: boolean | null;
        passed?: boolean | null;
        score?: number | null;
        notes?: string | null;
      } = {
        session_id: sessionId,
        user_id: userId,
        level,
        is_graded: false, // Writing module answers are graded later
        module: 'writing',
        exam_type: examType,
        is_success: true,
        passed: null,
        score: null,
        notes: null,
      };

      await saveSimulationResult(result);
      logger.debug('Simulation result saved, AI analysis job enqueued');
      logger.event(
        'simulation.writing.finish.completed',
        {
          sessionId,
          userId,
          answerCount: answers.length,
        },
        'info',
        'Writing module finish completed',
      );
      draft.clear();
    } catch (error) {
      logger.error('Failed to save writing module answers:', error, {
        event: 'simulation.writing.finish.failed',
        sessionId,
        userId,
      });
      throw error;
    }
  }, [
    simulationData,
    sessionId,
    userId,
    essayContentByTask,
    fillBlankAnswers,
    writingData,
    level,
    examType,
    draft,
  ]);

  // Current state
  const state: WritingModuleState = useMemo(
    () => ({
      currentChunk,
      essayContentByTask,
      fillBlankAnswers,
      notesByTask,
      timeRemaining,
    }),
    [currentChunk, essayContentByTask, fillBlankAnswers, notesByTask, timeRemaining],
  );

  return {
    state,
    essayTasks: writingData.essayTasks,
    currentEssayTask,
    currentEssayContent,
    fillBlankForms: writingData.fillBlankForms,
    hasFillBlank: writingData.hasFillBlank,
    hasEssay: writingData.hasEssay,
    totalChunks,
    isLoadingData,
    dataError,
    sessionId,
    currentNotes,
    setCurrentEssayContent,
    setFillBlankAnswer,
    setCurrentNotes,
    navigateChunk,
    goToChunk,
    saveAnswersAndResult,
  };
}
